import moment, { Moment } from "moment";
import "moment/locale/es";
import "moment/locale/en-gb";
import { cx } from "shared/utils/classnames";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import { IconButton, IconButtonSize } from "shared/components/IconButton";
import { capitalize } from "shared/utils/capitalize";
import type { IconSvgElement } from "@hugeicons/react";
import {
  ArrowLeft02Icon,
  ArrowRight02Icon
} from "@hugeicons-pro/core-stroke-rounded";

interface DatePickerProps {
  onSelectDate: (date: string) => void; // now ISO string
  selectedDate?: string; // now ISO string
  maxDays?: number | null;
  allowPastDates?: boolean;
  minDate?: string;
}

const getWeekday = (weekday: number) => {
  return moment().weekday(weekday).format("dddd");
};

function getDaysInMonth(month: number, year: number): Moment[] {
  const start = moment([year, month]);
  const days: Moment[] = [];
  const daysInMonth = start.daysInMonth();
  for (let i = 0; i < daysInMonth; i++) days.push(start.clone().add(i, "days"));

  return days;
}

export const DatePicker: FC<DatePickerProps> = ({
  onSelectDate,
  selectedDate,
  allowPastDates = false,
  minDate,
  maxDays
}) => {
  const initialMoment = moment(selectedDate ?? minDate ?? undefined);
  const [month, setMonth] = useState(initialMoment.month());
  const [year, setYear] = useState(initialMoment.year());

  const view = useMemo(() => {
    const thisMonthDates = getDaysInMonth(month, year);
    if (!thisMonthDates.length) return [];

    const first = thisMonthDates[0];
    const last = thisMonthDates[thisMonthDates.length - 1];

    const prevDays = Array.from({ length: first.day() }, (_, i) =>
      first.clone().subtract(first.day() - i, "days")
    );

    const nextDays = Array.from({ length: 6 - last.day() }, (_, i) =>
      last.clone().add(i + 1, "days")
    );

    return [...prevDays, ...thisMonthDates, ...nextDays];
  }, [month, year, selectedDate]);

  const nextMonth = () => {
    const next = moment([year, month]).add(1, "month");
    setMonth(next.month());
    setYear(next.year());
  };

  const prevMonth = () => {
    const prev = moment([year, month]).subtract(1, "month");
    setMonth(prev.month());
    setYear(prev.year());
  };

  const setDateMonth = (date: Moment) => {
    setMonth(date.month());
    setYear(date.year());
  };

  useEffect(() => {
    if (selectedDate) {
      const m = moment(selectedDate);
      setMonth(m.month());
      setYear(m.year());
    }
  }, [selectedDate]);

  const currentMonth = moment([year, month]);

  return (
    <div className={cx(view.length !== 42 ? "mb-10" : "")}>
      <div className="flex items-center justify-between border-b border-alpha-100 px-4 py-3">
        <span className="text-base font-medium text-content-1000">
          {capitalize(currentMonth.format("MMMM YYYY"))}
        </span>
        <div className="flex gap-2">
          <IconButton
            HugeIcon={ArrowLeft02Icon as IconSvgElement}
            disabled={
              !allowPastDates &&
              currentMonth.isSameOrBefore(
                minDate ? moment(minDate) : moment(),
                "month"
              )
            }
            onClick={prevMonth}
            size={IconButtonSize.SM}
          />
          <IconButton
            HugeIcon={ArrowRight02Icon as IconSvgElement}
            onClick={nextMonth}
            size={IconButtonSize.SM}
          />
        </div>
      </div>
      <div className="mx-auto grid grid-cols-7 justify-between justify-items-center text-center">
        {[0, 1, 2, 3, 4, 5, 6].map((d) => (
          <div
            key={d}
            className="flex h-10 w-full select-none items-center justify-center text-sm text-content-300"
          >
            {getWeekday(d)[0]}
          </div>
        ))}
        {view.map((date) => {
          const dateISO = date.format("YYYY-MM-DD");
          const isSelected = selectedDate
            ? moment(selectedDate).isSame(date, "day")
            : false;
          const isCurrentMonth = date.month() === month;
          let effectiveMin: moment.Moment | null;
          if (minDate) effectiveMin = moment(minDate).startOf("day");
          else if (allowPastDates) effectiveMin = null;
          else effectiveMin = moment().startOf("day");

          const maxBase = minDate ? moment(minDate) : moment();
          const effectiveMax =
            (maxDays ?? null) !== null
              ? maxBase.clone().startOf("day").add(maxDays, "days")
              : null;

          const isDisabled =
            !isCurrentMonth ||
            (effectiveMin !== null && date.isBefore(effectiveMin, "day")) ||
            (effectiveMax !== null && date.isAfter(effectiveMax, "day"));

          if (isDisabled)
            return (
              <div
                key={dateISO}
                className={cx(
                  "flex h-10 w-full shrink-0 select-none items-center justify-center rounded-md text-base",
                  isSelected
                    ? "bg-primary-500 font-bold text-white"
                    : "text-content-100"
                )}
              >
                {date.date()}
              </div>
            );

          return (
            <div
              key={dateISO}
              className={cx(
                "flex h-10 w-full shrink-0 cursor-pointer select-none items-center justify-center rounded-md text-base",
                isSelected
                  ? "bg-primary-500 font-bold text-white"
                  : "font-medium text-content-1000 hover:bg-primary-20"
              )}
              onClick={() => {
                onSelectDate(dateISO);
                setDateMonth(date);
              }}
              onKeyDown={() => {}}
            >
              {date.date()}
            </div>
          );
        })}
      </div>
    </div>
  );
};
